import type { InboxRow } from "../../../server/core/inbox";
import type { ScreenContext } from "../../shell/types";
import { live, publishCounts } from "../../shell/live";
import { api, el } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { ib } from "../../ui/record";
import { fillPager, listPer, rememberPer, searchBox } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { pageHead } from "../empty";
import { connectedScreen } from "./connected";
import { documentScreen } from "./document";
import { itemScreen } from "./item";
import { outboxScreen } from "./outbox";
import { scansScreen } from "./scans";

// The shared inbox (brief 1B-3, mockup Inbox): All open with its counts, Needs action, New to us,
// Documents, Flags, Notifications, Done and Filtered out, each a list of messages (mockup
// inboxList). A row opens the item (or, in Documents, the document waiting for review).
// Connected inboxes is its own screen (connected.ts). With AI off nothing is sorted further:
// everything lands in Needs action for a person to sort, linked only when the sender matches one
// record.

/** What changed in the inbox: the shell redraws the badge and the left menu's counts. */
export const inboxChanged = () => window.dispatchEvent(new Event("fc:inbox-changed"));

export interface InboxCounts {
  open: number;
  overview: number;
  work: number;
  new: number;
  docs: number;
  flags: number;
  fyi: number;
}

/** The line under each screen's title (mockup, with AI off). */
const LINES: Record<string, (n: number) => string> = {
  overview: (n) => `Everything from every business in one place. ${n} open.`,
  work: () => "Someone has to answer or do something.",
  new: () => "Not in the system yet: a new client, patient referral or candidate.",
  docs: (n) =>
    `Everything candidates, workers, caregivers and drivers upload lands here for review. ${n} waiting.`,
  flags: () => "Clear when the problem is fixed.",
  fyi: () => "Nothing to do. Read and archive.",
  done: () => "Answered, handled or archived.",
  filtered: () => "Kept out of the inbox. Bring back anything that belongs.",
};

const BIZ_ICON: Record<string, string> = {
  "Home Care": "heart",
  Staffing: "briefcase",
  Transportation: "location",
  "Job board": "megaphone",
};

/** One row of the list (mockup inboxList), keyed by id and stamp so a live update reuses it. */
function rowButton(r: InboxRow, go: (path: string) => void): HTMLElement {
  const b = el(
    "button",
    {
      type: "button",
      class: `imsg${r.open ? "" : " read"}`,
      role: "listitem",
      "data-id": r.id,
      "data-stamp": r.stamp,
    },
    el("span", { class: "ich" }, icon(r.icon)),
    el(
      "span",
      { class: "imain" },
      el("span", { class: "itop" }, el("b", {}, r.from), el("small", {}, r.when)),
      el("span", { class: "isub" }, r.subject),
      el(
        "span",
        { class: "ilab" },
        el("span", { class: `chip ${r.level}` }, r.label),
        r.business
          ? el(
              "span",
              { class: "bizt" },
              icon(BIZ_ICON[r.business] ?? "organization"),
              ` ${r.business}`,
            )
          : null,
        r.attachments
          ? el("span", { class: "bizt" }, icon("paperclip"), ` ${r.attachments}`)
          : null,
        r.assignee ? el("span", { class: "bizt" }, icon("person"), ` ${r.assignee}`) : null,
      ),
    ),
  );
  b.addEventListener("click", () => go(r.path));
  return b;
}

const inboxZero = () =>
  el(
    "div",
    { class: "blank" },
    icon("check-circle", "big"),
    el("h2", {}, "Inbox zero"),
    el("p", { class: "muted" }, "Nothing waiting. New messages land here as they come in."),
  );

/**
 * The mockup's list of messages, or "Inbox zero". update() takes what changed and the list's
 * order (brief 1B-6): a row whose stamp didn't move is kept as it is; a row no longer listed goes.
 */
export function inboxList(
  rows: readonly InboxRow[],
  go: (path: string) => void,
): { el: HTMLElement; update: (changed: readonly InboxRow[], order: readonly string[]) => void } {
  const wrap = el("div", { class: "inboxwrap" });
  const byId = new Map(rows.map((r) => [r.id, r]));
  let order = rows.map((r) => r.id);
  const buttons = new Map<string, HTMLElement>();
  const draw = () => {
    if (!order.length) return wrap.replaceChildren(inboxZero());
    let list = wrap.querySelector<HTMLElement>(".inbox");
    if (!list) {
      list = el("div", { class: "inbox", role: "list", "aria-label": "Messages" });
      wrap.replaceChildren(list);
    }
    const kids: HTMLElement[] = [];
    for (const id of order) {
      const r = byId.get(id);
      if (!r) continue;
      let b = buttons.get(id);
      if (!b || b.dataset.stamp !== r.stamp) {
        b = rowButton(r, go);
        buttons.set(id, b);
      }
      kids.push(b);
    }
    // Only rows that moved or changed are touched.
    const same =
      kids.length === list.children.length && kids.every((k, i) => list.children[i] === k);
    if (!same) list.replaceChildren(...kids);
  };
  draw();
  return {
    el: wrap,
    update(changed, next) {
      for (const r of changed) byId.set(r.id, r);
      order = next.filter((id) => byId.has(id));
      for (const id of [...buttons.keys()]) if (!order.includes(id)) buttons.delete(id);
      draw();
    },
  };
}

interface Changes {
  cursor: string;
  counts: InboxCounts;
  rows?: InboxRow[];
  order?: string[];
}

export async function inboxScreen(host: HTMLElement, ctx: ScreenContext) {
  const screen = ctx.screen.id;
  if (screen === "sources") return connectedScreen(host, ctx);
  if (screen === "outbox") return outboxScreen(host, ctx);
  if (screen === "scans") return scansScreen(host, ctx);
  if (ctx.record)
    return screen === "docs"
      ? documentScreen(host, ctx, ctx.record)
      : itemScreen(host, ctx, ctx.record);

  // Brief 1E-32: search and pages, asked of the server; the live check reads the same page.
  const listId = `inbox/${screen}`;
  let q = "";
  let pageNo = 1;
  let per = listPer(listId, ctx.me.user.id);
  const paging = () =>
    new URLSearchParams({
      ...(q.trim() ? { q: q.trim() } : {}),
      page: String(pageNo),
      per: String(per),
    });
  type Answer = PageInfo & { rows: InboxRow[]; admin: boolean; cursor: string };
  const ask = () => api<Answer>("GET", `/api/inbox?screen=${screen}&${paging().toString()}`);
  const [res, counts] = await Promise.all([
    ask(),
    screen === "overview" ? api<InboxCounts>("GET", "/api/inbox/counts") : null,
  ]);
  if (!res.ok) {
    host.append(
      pageHead(ctx),
      el("p", { class: "note", role: "alert" }, "The inbox couldn't be loaded."),
    );
    return;
  }
  const rows = res.body.rows;
  let info: PageInfo = res.body;
  const actions = res.body.admin
    ? [ib("plug", "Connected inboxes", () => ctx.go("/inbox/sources"))]
    : [];
  const n = screen === "overview" ? (counts?.body.open ?? res.body.total) : res.body.total;
  // The mockup's All open is titled "Inbox".
  const head = pageHead(ctx, (LINES[screen] ?? LINES.work)?.(n), actions);
  const subline = head.querySelector<HTMLElement>(".t > .muted");
  if (screen === "overview") {
    const h1 = head.querySelector("h1");
    if (h1) h1.replaceChildren("Inbox");
  }
  host.append(head);
  const cardCounts = new Map<string, HTMLElement>();
  if (screen === "overview") {
    host.append(
      el(
        "p",
        { class: "small muted", style: "margin:0" },
        icon("sync"),
        " Updates by itself · new messages and calls appear as they arrive",
      ),
      el(
        "div",
        { class: "note warnnote" },
        el(
          "p",
          {},
          icon("circle-slash"),
          " ",
          el("b", {}, "AI sorting is off."),
          " New mail isn't sorted by AI. It's linked when the sender matches one client or person, and lands in Needs action.",
        ),
      ),
    );
    const c = counts?.ok ? counts.body : null;
    const cards = el(
      "div",
      { class: "stagecards" },
      ...(
        [
          ["work", "Needs action"],
          ["new", "New contacts"],
          ["flags", "Flags"],
          ["fyi", "Notifications"],
        ] as const
      ).map(([k, label]) => {
        const num = el("b", {}, String(c?.[k] ?? 0));
        cardCounts.set(k, num);
        const b = el("button", { type: "button", class: "scard" }, num, el("span", {}, label));
        b.addEventListener("click", () => ctx.go(`/inbox/${k}`));
        return b;
      }),
    );
    host.append(cards);
  }
  const list = inboxList(rows, ctx.go);
  const pager = el("nav", { class: "pager", "aria-label": "Pages of messages" });
  let asked = 0;
  const again = async () => {
    const n = ++asked;
    const got = await ask();
    if (n !== asked || !got.ok) return;
    info = got.body;
    pageNo = info.page;
    cursor = got.body.cursor;
    list.update(
      got.body.rows,
      got.body.rows.map((r) => r.id),
    );
    drawPager();
  };
  const drawPager = () =>
    fillPager(pager, info, per, {
      per: async (n) => {
        per = n;
        rememberPer(listId, ctx.me.user.id, n);
        pageNo = 1;
        await again();
      },
      page: async (n) => {
        pageNo = n;
        await again();
      },
    });
  drawPager();
  host.append(
    searchBox("Search messages", (text) => {
      q = text;
      pageNo = 1;
      void again();
    }),
    list.el,
    pager,
  );

  // Live (brief 1B-6): every 15 seconds while the tab is visible, what changed since the last
  // answer. The counts go to the top bar and the left menu; only changed rows are redrawn.
  let cursor = res.body.cursor;
  live.watch("inbox", async () => {
    if (!host.isConnected) return false;
    const got = await api<Changes>(
      "GET",
      `/api/inbox/changes?screen=${encodeURIComponent(screen)}&since=${encodeURIComponent(cursor)}&${paging().toString()}`,
      undefined,
      { background: true },
    );
    if (!got.ok || !host.isConnected) return host.isConnected;
    cursor = got.body.cursor;
    publishCounts(got.body.counts);
    for (const [k, num] of cardCounts)
      num.textContent = String(got.body.counts[k as keyof InboxCounts]);
    if (got.body.rows && got.body.order) list.update(got.body.rows, got.body.order);
    const open = screen === "overview" ? got.body.counts.open : (got.body.order?.length ?? 0);
    const line = (LINES[screen] ?? LINES.work)?.(open);
    if (subline && line && subline.textContent !== line) subline.textContent = line;
    return true;
  });
}
